import { DropdownField, TextField } from './FormComponents';
import { type User } from 'shared/modelTypes';
import { Suspense } from './Suspense';
import { rpc } from '../backend';
import { Avatar } from './Avatar';
import { Button } from './Button';

type StatisticsViewProps = {
	users: User[];
};

type Preset = 'today' | 'thisMonth' | 'previousMonth' | '2months' | '3months' | 'year';

function convertToUTC(date: Date) {
	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
	.toISOString()
	.slice(0, 10);
}

function applyPreset(preset: Preset) {
	const now = new Date();
	let start: Date;
	let end: Date;

	switch (preset) {
		case 'today':
			start = now;
			end = now;
			break;
		case 'thisMonth':
			start = new Date(now.getFullYear(), now.getMonth(), 1);
			end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
			break;
		case 'previousMonth':
			start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
			end = new Date(now.getFullYear(), now.getMonth(), 0);
			break;
		case '2months':
			start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
			end = new Date(now.getFullYear(), now.getMonth(), 0);
			break;
		case '3months':
			start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
			end = new Date(now.getFullYear(), now.getMonth(), 0);
			break;
		case 'year':
			start = new Date(now.getFullYear(), 0, 1);
			end = new Date(now.getFullYear(), 11, 31);
			break;
	}

	return [convertToUTC(start), convertToUTC(end)] as const;
}

export function StatisticsView({ users }: StatisticsViewProps) {
	const root = document.createElement('div');
	root.className = 'flex flex-col gap-6';

	let selectedUserId: string | undefined = 'all';
	let includeArchived = false;
	let statsContainer: HTMLElement | null = null;

	const resetStats = () => {
		statsContainer?.remove();
		statsContainer = null;
	};

	const formContainer = (
		<div class="w-fit rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
			{!users.length && <small>No Users.</small>}
			{!!users.length && (
				<>
					<DropdownField<string>
						name="user"
						label="Users"
						options={[
							{ value: 'all', label: 'All Users' },
							...users.filter(u => u._id).map(u => ({
								value: u._id!,
								label: `${u.firstName} ${u.lastName}`,
							})),
						]}
						value={selectedUserId}
						onChange={(val) => {
							selectedUserId = val;
							const [todayStart, todayEnd] = applyPreset('today');
							if (startInput) startInput.value = todayStart;
							if (endInput) endInput.value = todayEnd;
							loadStats();
						}}
					/>
					<div class="mt-4 flex flex-wrap gap-2">
						{(['today','thisMonth','previousMonth','2months','3months','year'] as Preset[]).map((key) => (
							<Button
								type="button"
								on:click={() => {
									const [start, end] = applyPreset(key);
									const startInput = formContainer.querySelector<HTMLInputElement>('input[name="startDate"]');
									const endInput = formContainer.querySelector<HTMLInputElement>('input[name="endDate"]');
									if (startInput) startInput.value = start;
									if (endInput) endInput.value = end;
									loadStats();
								}}
							>
								{{
									today: 'Vandaag',
									thisMonth: 'Deze maand',
									previousMonth: 'Vorige maand',
									'2months': 'Afgelopen 2 maanden',
									'3months': 'Afgelopen 3 maanden',
									year: 'Dit jaar',
								}[key]}
							</Button>
						))}
					</div>

					<div class="mt-4 flex gap-4">
						<TextField
							label="Start Datum"
							name="startDate"
							type="date"
							value=""
							labelWidth="10ch"
						/>
						<TextField
							label="Eind Datum"
							name="endDate"
							type="date"
							value=""
							labelWidth="10ch"
						/>
					</div>
					<label class="mt-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
						<input
							type="checkbox"
							on:change={(e: Event) => {
								includeArchived = (e.target as HTMLInputElement).checked;
								loadStats();
							}}
						/>
						<span>Toon gearchiveerd</span>
					</label>
				</>
			)}
		</div>
	) as unknown as HTMLElement;

	const startInput = formContainer.querySelector<HTMLInputElement>('input[name="startDate"]');
	const endInput = formContainer.querySelector<HTMLInputElement>('input[name="endDate"]');

	if (startInput) startInput.addEventListener('input', () => loadStats());
	if (endInput) endInput.addEventListener('input', () => loadStats());

	const loadStats = () => {
		if (!selectedUserId) return;
		resetStats();

		const startDate = startInput?.value || undefined;
		const endDate = endInput?.value || undefined;

		const newStatsContainer = document.createElement('div');
		newStatsContainer.className = 'stats-container';

		const suspenseElement = (
			<Suspense
				key={`${selectedUserId}-${startDate}-${endDate}-${includeArchived}`}
				query={() =>
					selectedUserId === 'all'
						? rpc.getAllUsersGameStats.query({ startDate, endDate, includeArchived })
						: rpc.getUserGameStats.query({
								userId: selectedUserId!,
								startDate,
								endDate,
								includeArchived,
						})
				}
				onLoaded={(stats) => {
					const totalSessions = stats.sessions.length;
					return (
						<div class="mt-6 flex max-w-[700px] flex-col gap-6">
							<div class="rounded-lg bg-blue-50 p-4 shadow-md dark:bg-blue-900">
								<div class="flex items-center justify-between">
									<span class="text-lg font-semibold text-gray-700 dark:text-gray-200">
										Totaal aantal speelsessies:
									</span>
									<span class="text-2xl font-bold text-blue-600 dark:text-blue-300">
										{totalSessions}
									</span>
								</div>
								{(startDate || endDate) && (
									<div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
										{startDate && `Van: ${new Date(startDate).toLocaleDateString('nl-NL')}`}
										{startDate && endDate && ' - '}
										{endDate && `Tot: ${new Date(endDate).toLocaleDateString('nl-NL')}`}
									</div>
								)}
							</div>

							<div>
								<h3 class="mb-3 text-lg font-bold text-gray-900 dark:text-gray-100">
									Speelsessies
								</h3>
								{!stats.sessions.length && (
									<small class="text-gray-700 dark:text-gray-300">
										Nog niet gespeeld in deze periode.
									</small>
								)}
								{!!stats.sessions.length && (
									<table class="min-w-full border-collapse overflow-hidden rounded-md bg-white text-sm shadow-md dark:bg-gray-800">
										<thead>
											<tr class="border-b border-gray-200 text-gray-800 dark:border-gray-700 dark:text-gray-100">
												<th class="p-3 text-left"></th>
												<th class="p-3 text-left">User</th>
												<th class="p-3 text-left">Game</th>
												<th class="p-3 text-left">Starttijd</th>
												<th class="p-3 text-right">Spelers</th>
											</tr>
										</thead>
										<tbody>
											{stats.sessions.map((session) => (
												<tr class="border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700">
													<td class="p-3">
														<Avatar
															src={`/images/${session.kind}.jpg`}
															alt="game icon"
														/>
													</td>
													<td class="p-3 font-medium text-gray-800 dark:text-gray-200">
														{session.userName}
													</td>
													<td class="p-3 font-medium text-gray-800 dark:text-gray-200">
														{session.gameName}
													</td>
													<td class="p-3 text-gray-700 dark:text-gray-300">
														{new Date(session.startTime).toLocaleString('nl-NL')}
													</td>
													<td class="p-3 text-right text-lg font-bold text-gray-700 dark:text-gray-300">
														{session.playerCount}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>
						</div>
					);
				}}
			/>
		) as HTMLElement;

		newStatsContainer.appendChild(suspenseElement);
		statsContainer = newStatsContainer;
		root.appendChild(statsContainer);
	};

	root.appendChild(formContainer);
	return root;
}